import { CONSENT_STATES, type CustomerBasis, type CustomerType } from '@/api/customers'
import { EmptyState } from '@/design'

import { Chip } from '../list/Chip'
import { consentLabel } from './columns'
import type { CustomerParams } from './params'

const BASES: readonly { value: CustomerBasis; label: string }[] = [
  { value: 'purchased', label: 'Bought in period' },
  { value: 'registered', label: 'Signed up in period' },
  { value: 'all', label: 'All customers' },
]

const TYPES: readonly { value: CustomerType | undefined; label: string }[] = [
  { value: undefined, label: 'Everyone' },
  { value: 'new', label: 'New' },
  { value: 'returning', label: 'Returning' },
]

/** A yes / no / either choice for a flag the website syncs. */
function FlagFilter({
  label,
  yes,
  no,
  value,
  onChange,
}: {
  label: string
  yes: string
  no: string
  value: boolean | undefined
  onChange: (value: boolean | undefined) => void
}) {
  return (
    <label className="ci-field ci-field--inline list-filter">
      <span>{label}</span>
      <select
        value={value === undefined ? '' : String(value)}
        onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value === 'true')}
      >
        <option value="">Any</option>
        <option value="true">{yes}</option>
        <option value="false">{no}</option>
      </select>
    </label>
  )
}

/**
 * Who is listed, and the filters that narrow them: new or returning in the period, and
 * the record's flags and marketing consent. A record filter leaves out customers whose
 * record has not synced, as the API does, and says so.
 */
export function CustomerFilters({ params }: { params: CustomerParams }) {
  const recordFiltered = params.member !== undefined || params.vip !== undefined || params.consent.length > 0
  return (
    <div className="cus-filters">
      <div className="list-chips" role="group" aria-label="Who is listed">
        {BASES.map((basis) => (
          <Chip key={basis.value} pressed={params.basis === basis.value} onClick={() => params.setBasis(basis.value)}>
            {basis.label}
          </Chip>
        ))}
      </div>
      <div className="list-chips" role="group" aria-label="Customer type">
        {TYPES.map((type) => (
          <Chip key={type.label} pressed={params.customerType === type.value} onClick={() => params.setCustomerType(type.value)}>
            {type.label}
          </Chip>
        ))}
      </div>
      <div className="list-toolbar__end">
        <FlagFilter label="Members" yes="Members only" no="Non-members" value={params.member} onChange={params.setMember} />
        <FlagFilter label="VIP" yes="VIPs only" no="Non-VIPs" value={params.vip} onChange={params.setVip} />
      </div>
      <div className="list-chips" role="group" aria-label="Marketing consent">
        <span className="list-muted list-hint">Marketing consent</span>
        {CONSENT_STATES.map((state) => (
          <Chip key={state} pressed={params.consent.includes(state)} onClick={() => params.toggleConsent(state)}>
            {consentLabel(state)}
          </Chip>
        ))}
      </div>
      {recordFiltered ? (
        <p className="list-note">Customers whose record has not synced from the website yet are left out while a member, VIP or consent filter is on.</p>
      ) : null}
    </div>
  )
}

/** Why the list is empty: the page's own filters, the filter bar's scope, or nobody in the period. */
export function NoRows({ params, scoped }: { params: CustomerParams; scoped: boolean }) {
  const filtered =
    params.customerType !== undefined || params.member !== undefined || params.vip !== undefined || params.consent.length > 0
  if (filtered) {
    return (
      <EmptyState
        variant="no-results"
        compact
        title="No customers match these filters"
        description="Try other filters, or clear them."
        action={
          <button type="button" className="ci-button" onClick={params.clearFilters}>
            Clear filters
          </button>
        }
      />
    )
  }
  if (scoped) {
    return (
      <EmptyState
        variant="no-results"
        compact
        title="No customers in this scope"
        description="No customer belongs to the website or environments chosen in the filter bar. Try another."
      />
    )
  }
  return (
    <EmptyState
      variant="no-data"
      compact
      title={params.basis === 'registered' ? 'No sign-ups in this period' : params.basis === 'all' ? 'No customers yet' : 'No customers bought in this period'}
      description={
        params.basis === 'all'
          ? 'Customers appear here once your website’s customers and orders are syncing.'
          : 'Choose All customers to list everyone, or another period.'
      }
    />
  )
}
